import { useRef, useState } from "react";
import type { WidgetProps } from "../registry";
import { client } from "../../../lib/api";
import type { AssessmentPayload } from "../../../generated-api";
import { useVideoSelector } from "../../../hooks/useVideoSearch";
import VideoPlayer from "../../programs/VideoPlayer";
import ProgramMarkDoneModal from "../../programs/ProgramMarkDoneModal";

export function AssessmentWidget({
  payload,
  onComplete,
}: WidgetProps<AssessmentPayload>) {
  const [error, setError] = useState<string | null>(null);
  const [isMarkDoneOpen, setIsMarkDoneOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const { results, selectedVideo, selectedIndex, setSelectedIndex, isLoading } =
    useVideoSelector(payload.exerciseSearchQuery);

  const handleComplete = async (
    painScore: number | null,
    notes: string | null,
  ) => {
    if (!selectedVideo?.embedUrl) return;

    setError(null);

    try {
      const data = await client.assessments.postAssessments({
        requestBody: {
          videoId: undefined,
          embedUrl: selectedVideo.embedUrl,
          targetId: payload.targetId,
          painScore: painScore ?? undefined,
          notes: notes?.trim() || undefined,
        },
      });

      onComplete(data);
      setIsMarkDoneOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    }
  };

  return (
    <div className="mt-1 flex w-full min-w-0 max-w-2xl flex-col gap-3 overflow-x-hidden">
      {payload.title && (
        <p className="text-sm font-semibold text-text-primary">
          {payload.title}
        </p>
      )}
      {payload.instructions && typeof payload.instructions === "string" && (
        <p className="text-xs text-text-secondary">{payload.instructions}</p>
      )}
      {payload.instructions && typeof payload.instructions === "object" && (
        <div className="text-xs text-text-secondary flex flex-col gap-1">
          {payload.instructions.headline && (
            <p>{payload.instructions.headline}</p>
          )}
          {payload.instructions.steps &&
            payload.instructions.steps.length > 0 && (
              <ol className="list-decimal list-inside flex flex-col gap-1">
                {payload.instructions.steps.map((step: string, i: number) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            )}
        </div>
      )}
      <div className="aspect-video min-h-[220px] w-full overflow-hidden rounded-lg border border-border-subtle bg-black sm:min-h-[280px]">
        {selectedVideo?.embedUrl ? (
          <VideoPlayer
            videoRef={videoRef}
            currentEmbedUrl={selectedVideo.embedUrl}
            title={selectedVideo.title ?? payload.title ?? "Assessment video"}
            className="h-full w-full"
            onError={() => setError("Unable to load video")}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-xs text-text-tertiary">
            {isLoading ? "Loading videos..." : "No video available"}
          </div>
        )}
      </div>

      {/* SIMPLE selector UI, still widget-owned */}
      {results.length > 1 && (
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 min-h-8">
          {results.map((v, i) => (
            <button
              key={v.url}
              type="button"
              onClick={() => setSelectedIndex(i)}
              className={`flex-none whitespace-nowrap text-xs px-2 py-1 rounded border ${
                i === selectedIndex
                  ? "bg-indigo-600 text-white"
                  : "border-border"
              }`}
            >
              {v.title?.slice(0, 25) ?? `Video ${i + 1}`}
            </button>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setIsMarkDoneOpen(true)}
        disabled={!selectedVideo}
        className="self-start rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-40"
      >
        Mark complete
      </button>
      {error && <p className="text-xs text-red-500">{error}</p>}
      {isMarkDoneOpen && (
        <ProgramMarkDoneModal
          clip={undefined}
          title={selectedVideo?.title ?? payload.title ?? "Assessment"}
          notesRequired
          onSave={handleComplete}
          onClose={() => setIsMarkDoneOpen(false)}
        />
      )}
    </div>
  );
}
